import { useQuery } from "@tanstack/react-query";

import { GraphQLAPI, GraphQLResult, queries } from "@examspark/api";
import {
  collectAllPages,
  getBankIdFromData,
  getTagFromResource,
  hasValue,
} from "@examspark/common";
import { ModelWorkspaceConnection } from "@examspark/types";

import { useAppContext } from "../../providers/AppContext";

import { useQueryListContentSharedToWorkspace } from "./sharing";

export const useQueryListBanks = (workspaceId?: string) => {
  const [state] = useAppContext();

  workspaceId = workspaceId ?? state.workspaceUser?.id;

  return useQuery({
    enabled: !!workspaceId,
    queryKey: ["banks"],
    queryFn: async () =>
      collectAllPages(async (nextToken) => {
        const result = (await GraphQLAPI.query({
          query: queries.workspace.listBanks,
          variables: {
            input: { id: workspaceId, nextToken },
          },
        })) as GraphQLResult<{ listBanks: ModelWorkspaceConnection }>;

        return result.data?.listBanks;
      }),
  });
};

export const useQueryGetBank = (bankId: string | null | undefined) => {
  const banksQuery = useQueryListBanks();
  const sharesQuery = useQueryListContentSharedToWorkspace();

  if (bankId == null) return null;

  const bank = banksQuery.data
    ?.flatMap((p) => p.items ?? [])
    ?.concat(sharesQuery.data?.flatMap((r) => r.banks ?? []) ?? [])
    .filter(hasValue)
    .find((b) => getBankIdFromData(b) === bankId);

  return bank;
};

export const useQueryGetBankLabels = (
  bankIds: Array<string | undefined> | undefined,
) => {
  const banksQuery = useQueryListBanks();
  const sharesQuery = useQueryListContentSharedToWorkspace();

  return useQuery({
    enabled: !!bankIds && banksQuery.data != null && sharesQuery.data != null,
    queryKey: ["bank-label", bankIds],
    queryFn: () => {
      return bankIds?.map((bankId) => {
        const bank =
          banksQuery.data
            ?.flatMap((r) => r.items ?? [])
            .find((b) => b != null && getBankIdFromData(b) === bankId) ??
          sharesQuery.data
            ?.flatMap((r) => r.banks ?? [])
            .filter(hasValue)
            .find((b) => getBankIdFromData(b) === bankId);

        if (bank == null) return undefined;

        return (
          getTagFromResource(bank, "bonfire-label")?.value ??
          bank.name ??
          getBankIdFromData(bank)
        );
      });
    },
  });
};
